/**
 * Shared media attachment zone — drag-drop file upload with thumbnails.
 * Used by Content Studio, Library drawer, and Campaign drawer.
 */

export function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/**
 * Shape-tolerant URL accessor for a media item.
 *
 * Two flavors of media object circulate in this codebase:
 *   - **Browser-side (file-upload)** — `{ name, type:"image/jpeg", dataUrl: <objectUrl|base64>, size }`.
 *     Produced by FileReader / URL.createObjectURL when the user drops a file.
 *   - **Server-side (canonical)** — `{ url:"https://…", type:"image", thumbUrl?, libraryId?, source?, ... }`.
 *     Produced by `update_draft.mediaUrls` / `add_media_to_draft` / mediaResolver
 *     and stored in `content_items.media_json`.
 *
 * The renderers historically only read `dataUrl`, so any server-attached
 * image rendered as a broken thumbnail. These helpers smooth over the
 * difference at render time so neither write path is "wrong" anymore.
 */
export function mediaSrc(m) {
  if (!m) return '';
  return m.dataUrl || m.url || m.thumbUrl || '';
}
export function mediaIsVideo(m) {
  if (!m) return false;
  const t = String(m.type || '').toLowerCase();
  if (t.startsWith('video/') || t === 'video') return true;
  // Fallback: extension check on whatever URL we have.
  return /\.(mp4|mov|webm|m4v)(\?|$)/i.test(mediaSrc(m));
}
export function mediaAlt(m) {
  if (!m) return 'media';
  return m.altText || m.name || m.originalName || m.filename || 'media';
}

/**
 * Returns the HTML for the media drop zone + attached media list.
 * @param {Array} attachedMedia - Array of media objects
 * @param {string} [inputId='editor-media-input'] - ID for the hidden file input
 */
export function renderMediaZone(attachedMedia = [], inputId = 'editor-media-input') {
  const listHtml = attachedMedia.length > 0 ? `
    <div class="media-attached-list" data-testid="media-attached-list">
      ${attachedMedia.map((m, i) => `
        <div class="media-attached-item" data-media-index="${i}">
          <div class="media-attached-thumb">
            ${mediaIsVideo(m) ? `
              <div class="media-video-thumb">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              </div>
            ` : `<img src="${mediaSrc(m)}" alt="${mediaAlt(m)}">`}
          </div>
          <div class="media-attached-info">
            <div class="media-attached-name">${mediaAlt(m)}</div>
            <div class="media-attached-meta">${formatFileSize(m.size || m.fileSize || 0)}${m.dimensions ? ' - ' + m.dimensions : ''}</div>
          </div>
          <button class="media-attached-remove" data-remove-media="${i}" data-testid="remove-media-${i}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      `).join('')}
    </div>
  ` : '';

  return `
    <div class="media-zone-wrapper">
      <label class="create-label" style="margin-bottom:6px;">Attach Media</label>
      <input type="file" id="${inputId}" accept="image/*,video/*" multiple style="display:none" data-testid="${inputId}">
      <label for="${inputId}" class="media-zone media-zone--editor" data-testid="editor-media-zone" role="button" tabindex="0">
        <div class="media-zone-ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg></div>
        <div class="media-zone-lbl">Tap to attach image or video</div>
        <div class="media-zone-sub">JPG, PNG, GIF, HEIC, MP4, MOV - Max 500MB</div>
      </label>
      ${listHtml}
    </div>
  `;
}

/**
 * Process file objects into media data objects.
 * @param {FileList} files
 * @param {Array} attachedMedia - Current media array (mutated in place)
 * @param {Function} onUpdate - Called after each file is processed with the updated array
 */
export function handleMediaFiles(files, attachedMedia, onUpdate) {
  const maxSize = 500 * 1024 * 1024;

  Array.from(files).forEach(file => {
    // Accept any image/* or video/* MIME type (covers HEIC, WebP, etc.)
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) return;
    if (file.size > maxSize) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const media = {
        name: file.name,
        size: file.size,
        type: file.type,
        dataUrl: ev.target.result,
        dimensions: null
      };

      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          media.dimensions = `${img.width}x${img.height}`;
          attachedMedia.push(media);
          onUpdate(attachedMedia);
        };
        img.src = ev.target.result;
      } else if (file.type.startsWith('video/')) {
        // Use Object URL for videos — avoids massive base64 data URLs
        media.dataUrl = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.preload = 'metadata';
        let handled = false;
        const push = () => { if (handled) return; handled = true; attachedMedia.push(media); onUpdate(attachedMedia); };
        video.onloadedmetadata = () => {
          const dur = Math.floor(video.duration);
          const mins = Math.floor(dur / 60);
          const secs = dur % 60;
          media.dimensions = `${mins}:${secs.toString().padStart(2, '0')}`;
          media.duration = dur;
          media.videoWidth = video.videoWidth;
          media.videoHeight = video.videoHeight;
          push();
        };
        video.onerror = () => push();
        setTimeout(() => push(), 3000);
        video.src = media.dataUrl;
        return; // Skip readAsDataURL for videos
      } else {
        attachedMedia.push(media);
        onUpdate(attachedMedia);
      }
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Bind all media zone event listeners (click, keyboard, drag-drop, remove).
 * @param {HTMLElement} container - Parent element containing the media zone
 * @param {Array} attachedMedia - Current media array
 * @param {Function} onUpdate - Called when media changes, receives updated array
 * @param {string} [inputId='editor-media-input'] - ID for the hidden file input
 */
export function bindMediaZoneEvents(container, attachedMedia, onUpdate, inputId = 'editor-media-input') {
  const mediaZone = container.querySelector('[data-testid="editor-media-zone"]');
  const mediaInput = container.querySelector(`#${inputId}`);

  if (mediaZone && mediaInput) {
    // Media zone is now a <label> — native click opens file picker, no need for click handler
    mediaZone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); mediaInput.click(); }
    });

    mediaInput.addEventListener('change', (e) => {
      if (e.target.files.length > 0) handleMediaFiles(e.target.files, attachedMedia, onUpdate);
      e.target.value = '';
    });

    mediaZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      mediaZone.classList.add('media-zone--dragover');
    });

    mediaZone.addEventListener('dragleave', (e) => {
      e.preventDefault();
      e.stopPropagation();
      mediaZone.classList.remove('media-zone--dragover');
    });

    mediaZone.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      mediaZone.classList.remove('media-zone--dragover');
      if (e.dataTransfer.files.length > 0) handleMediaFiles(e.dataTransfer.files, attachedMedia, onUpdate);
    });
  }

  // Remove media buttons
  container.querySelectorAll('[data-remove-media]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.dataset.removeMedia);
      attachedMedia.splice(idx, 1);
      onUpdate(attachedMedia);
    });
  });
}
